import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { startupService, apiClient, ApiError } from '@/services'
import { useAppStore } from '@/stores/appStore'
import type { StartupWithMembership } from '@/types/api'

interface BootstrapResult {
  /** True until the first list() resolves or rejects. */
  loading: boolean
  /** Set when the list fails for any reason other than 401 (which redirects). */
  error: string | null
  /** Manual retry trigger for the sticky banner. */
  retry: () => void
}

/**
 * App-shell bootstrap. Mounted ONCE in AppShell. Responsibilities:
 *
 *   1. Wire `apiClient.setUnauthorizedHandler` to the sign-out + redirect
 *      flow so any future 401 from any service short-circuits the app.
 *   2. Fetch the user's startups via `startupService.list()` and seed
 *      `appStore` based on the result:
 *        - 0 startups   → navigate to /onboarding (unless already there)
 *        - 1 startup    → setActiveStartup(it), no navigation
 *        - N startups   → setActiveStartup(first), setStartups(all)
 *   3. Surface a non-401 failure (network or 5xx) as a sticky banner
 *      with a retry button, instead of redirecting.
 *
 * The hook itself does NOT subscribe to updates — that's `useUpdatesStream`,
 * also mounted in AppShell, separately. Keeping bootstrap and updates
 * decoupled means re-running bootstrap (via retry) doesn't tear down the
 * SSE connection.
 *
 * Auth signOut is handled by the existing authStore; the unauthorized
 * handler defers to it via dynamic import to avoid a circular dependency
 * between services and stores.
 */
export function useStartupBootstrap(): BootstrapResult {
  const navigate = useNavigate()
  const setActiveStartup = useAppStore((s) => s.setActiveStartup)
  const setStartups = useAppStore((s) => s.setStartups)
  const clear = useAppStore((s) => s.clear)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Stable refs over store actions so the bootstrap effect doesn't refire
  // on every render when Zustand returns fresh-but-equal selector results.
  const setActiveStartupRef = useRef(setActiveStartup)
  const setStartupsRef = useRef(setStartups)
  setActiveStartupRef.current = setActiveStartup
  setStartupsRef.current = setStartups

  const runBootstrap = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const startups = await startupService.list()
      routeFromList(startups, {
        navigate,
        setActiveStartup: setActiveStartupRef.current,
        setStartups: setStartupsRef.current,
      })
    } catch (err) {
      // 401 is handled by the unauthorized handler below; just surface
      // any other failure as a banner-actionable error.
      if (err instanceof ApiError && err.status === 401) {
        setLoading(false)
        return
      }
      const message =
        err instanceof Error ? err.message : 'Failed to load your startups.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [navigate])

  useEffect(() => {
    apiClient.setUnauthorizedHandler(async () => {
      clear()
      try {
        const { useAuthStore } = await import('@/stores/authStore')
        await useAuthStore.getState().signOut()
      } catch {
        // Even if signOut fails (e.g. Firebase already cleared), still route.
      }
      navigate('/login', { replace: true })
    })

    void runBootstrap()

    return () => {
      apiClient.setUnauthorizedHandler(null)
    }
  }, [navigate, clear, runBootstrap])

  return { loading, error, retry: () => void runBootstrap() }
}

interface RouteContext {
  navigate: (path: string, options?: { replace?: boolean }) => void
  setActiveStartup: (entry: StartupWithMembership | null) => void
  setStartups: (list: StartupWithMembership[]) => void
}

function routeFromList(
  startups: StartupWithMembership[],
  ctx: RouteContext
): void {
  if (startups.length === 0) {
    ctx.setStartups([])
    ctx.setActiveStartup(null)
    if (typeof window !== 'undefined' && window.location.pathname !== '/onboarding') {
      ctx.navigate('/onboarding', { replace: true })
    }
    return
  }

  ctx.setStartups(startups)
  ctx.setActiveStartup(startups[0])

  if (startups.length > 1) {
    // Multi-startup org switcher is a Phase 2.5 / future feature. For now
    // we pick the first and log so we can audit how often it happens in
    // dogfood telemetry. The first item is whatever the backend returns
    // first — typically the most-recently-created.
    console.info(
      `[bootstrap] ${startups.length} startups for this user — defaulting to ${startups[0].startup.id}`
    )
  }
}
